// The sign-up verification step, persisted so it survives leaving the page.
//
// The common path through sign-up is to leave the tab, open an email client to
// read the code, and come back. This used to be held in sessionStorage, which
// a new tab does not share, so returning users landed on an empty sign-up form
// with their account already created and UNCONFIRMED. Re-submitting the same
// address then failed with UsernameExistsException, and prod has the duplicate
// accounts to show for it: the same person registering again minutes later on
// a second address.
//
// Only the Cognito username and the email are stored. The password is
// deliberately not, so a restored session confirms the account and routes to
// login rather than auto-signing in with a credential recovered from disk.

const PENDING_VERIFICATION_KEY = "kaagazy.signup.pendingVerification";

// Cognito expires a confirmation code after 24 h. Past that the stored record
// can only strand someone on a verify step whose code is already dead, so it
// is treated as absent and they get a clean form back.
export const PENDING_VERIFICATION_TTL_MS = 24 * 60 * 60 * 1000;

export type PendingVerification = { username: string; email: string };

type StoredPendingVerification = PendingVerification & { createdAt: number };

/** Removes the stored record. Safe to call when nothing is stored. */
export function clearPendingVerification(): void {
  try {
    localStorage.removeItem(PENDING_VERIFICATION_KEY);
  } catch {
    /* non-fatal: storage throws in Safari private mode */
  }
}

/**
 * Returns the in-flight verification, or null when there is none, it is
 * malformed, or its code has outlived Cognito's 24 h window. An expired or
 * malformed record is cleared as a side effect so it cannot be re-read.
 */
export function readPendingVerification(): PendingVerification | null {
  let parsed: Partial<StoredPendingVerification>;
  try {
    const raw = localStorage.getItem(PENDING_VERIFICATION_KEY);
    if (!raw) return null;
    parsed = JSON.parse(raw) as Partial<StoredPendingVerification>;
  } catch {
    // Unreadable storage or invalid JSON. Nothing to recover.
    return null;
  }

  if (!parsed?.username || !parsed?.email) {
    clearPendingVerification();
    return null;
  }

  // A record written before createdAt existed carries no timestamp. Treat it
  // as expired rather than trusting it indefinitely.
  if (
    typeof parsed.createdAt !== "number" ||
    Date.now() - parsed.createdAt > PENDING_VERIFICATION_TTL_MS
  ) {
    clearPendingVerification();
    return null;
  }

  return { username: parsed.username, email: parsed.email };
}

/**
 * Stores the in-flight verification, stamped so it can expire.
 *
 * The two fields are copied out by name rather than spread. A spread would
 * persist whatever else the caller happened to pass, and the one field that
 * must never reach disk here is the password.
 */
export function writePendingVerification(pending: PendingVerification): void {
  try {
    const stored: StoredPendingVerification = {
      username: pending.username,
      email: pending.email,
      createdAt: Date.now(),
    };
    localStorage.setItem(PENDING_VERIFICATION_KEY, JSON.stringify(stored));
  } catch {
    /* non-fatal: the flow still works as long as the tab stays open */
  }
}
